import { Spinner, Typography } from '@tempmee/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { hideEodInvitePopup, SHOW_MESSAGE, showEodInvitePopup } from '~/actions';
import browserHistory from '~/browserHistory';
import { colors } from '@tempmee/tailwind';
import { Box, Button, Center, Row } from '~/commonComponents';
import Portal from '~/commonComponents/Portal';
import { IEligibleJob } from '~/domain/EligibleJobs';
import {
  IInviteProfessionalsToJobsPayload,
  jobKeys,
  useInviteProfessionalsToJobsMutation,
} from '~/domain/Jobs';
import ClosePopupIcon from '~/images/ClosePopupIcon.svg';
import defaultProfile from '~/images/HY_tooth.png';
import { IUser } from '~/models';
import {
  fireEodInvitePopupViewed,
  fireEodNoThanksClicked,
  fireInviteSent,
  fireShiftSelected,
} from '~/posthogs/events';
import { routes } from '~/routes';
import { EligibleJobCard } from './EligibleJobCard';

interface IRatingSentSuccessPopupProps {
  eligibleJobs: IEligibleJob[];
  professional: IUser;
  officeId: string;
  onClose: () => void;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  onSubmitFeedback: () => void;
}

export const RatingSentSuccessPopup = ({
  eligibleJobs,
  professional,
  officeId,
  onClose,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  onSubmitFeedback,
}: IRatingSentSuccessPopupProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const inviteProfessionalsToJobsMutation = useInviteProfessionalsToJobsMutation();
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isInviting = inviteProfessionalsToJobsMutation.isPending;

  useEffect(() => {
    fireEodInvitePopupViewed({ professionalId: professional?.id });
  }, [professional?.id]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleNoThanks = useCallback(() => {
    fireEodNoThanksClicked({ professionalId: professional?.id });
    dispatch(hideEodInvitePopup());
    onClose();
  }, [professional?.id, onClose, dispatch]);

  const handleJobSelect = useCallback(
    (jobId: string) => {
      setSelectedJobIds((prev) => {
        if (prev.includes(jobId)) {
          return prev.filter((id) => id !== jobId);
        }

        fireShiftSelected({
          shiftId: jobId,
          shiftStatus:
            eligibleJobs.find((job) => job.id === jobId)?.status === 'open'
              ? 'Open'
              : 'Action Needed',
        });

        return [...prev, jobId];
      });
    },
    [eligibleJobs],
  );

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 50;

    if (scrolledToBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handlePopupClose = useCallback(() => {
    setSelectedJobIds([]);
    dispatch(hideEodInvitePopup());
    onClose();
  }, [onClose, dispatch]);

  const handleInviteSuccess = useCallback(
    (_data: unknown, payload: IInviteProfessionalsToJobsPayload) => {
      payload.jobIds.forEach((jobId) => {
        queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
      });
      handlePopupClose();
      dispatch({
        type: SHOW_MESSAGE,
        payload: {
          isSuccess: true,
          popupProps: {
            rightButtonText: 'Close',
          },
          route: '/',
          title: 'Request(s) Successfully Sent!',
          message: (
            <>
              <b>
                {professional?.first_name} {professional?.last_name}
              </b>{' '}
              has been invited to the selected shift(s). Once they accept at the listed rate,{' '}
              {professional?.first_name} will be confirmed!
            </>
          ),
        },
      });
    },
    [dispatch, handlePopupClose, professional, queryClient],
  );

  const handleInviteClick = () => {
    if (selectedJobIds.length > 0) {
      onSubmitFeedback();

      fireInviteSent({
        professionalId: professional?.id,
        totalInvitesSent: selectedJobIds.length,
        source: 'EOD Invite Pop-up',
      });

      inviteProfessionalsToJobsMutation.mutate(
        {
          professionalIds: [professional.id],
          jobIds: selectedJobIds,
          officeId,
        },
        {
          onSuccess: handleInviteSuccess,
        },
      );
    }
  };

  const onCardPress = (jobId: string) => {
    dispatch(showEodInvitePopup());
    browserHistory.push(routes.jobDetails.replace(':jobId', jobId));
  };

  return (
    <Portal>
      <Box
        className="modal"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
        }}
      >
        <Box
          className="modal_content"
          style={{
            position: 'relative',
            padding: '40px 30px',
            maxWidth: 700,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <Box style={{ position: 'absolute', top: 20, right: 20, cursor: 'pointer' }}>
            <img alt="close-popup-icon" src={ClosePopupIcon} onClick={handleNoThanks} />
          </Box>

          <Typography as="span" size="body_large" bold>
            Rating Sent Successfully!
          </Typography>

          <Box style={{ marginTop: 12, marginBottom: 16, textAlign: 'center' }}>
            {professional?.profile_image ? (
              <img
                src={professional.profile_image}
                style={{
                  width: 68,
                  height: 68,
                  borderRadius: 68,
                  objectFit: 'cover',
                }}
                alt="profile_image"
              />
            ) : (
              <Center
                backgroundColor={colors.secondary[500]}
                borderRadius={68}
                width={68}
                height={68}
              >
                <img src={defaultProfile} alt="profile_image" width="40px" height="40px" />
              </Center>
            )}
          </Box>

          <Typography as="span" color={colors.neutral[500]} textAlign="center">
            Sounds like{' '}
            <Typography as="span" bold color={colors.neutral[500]}>
              {professional?.first_name?.length > 20
                ? professional?.first_name?.slice(0, 20) + '...'
                : professional?.first_name}
            </Typography>{' '}
            was a great fit.
            <br />
            Would you like to invite them to upcoming shifts?
          </Typography>

          <Box
            ref={scrollContainerRef}
            flex={1}
            marginBottom={24}
            marginTop={16}
            overflowY="auto"
            overflowX="hidden"
            maxHeight="400px"
            scrollbarWidth="thin"
          >
            {isLoading ? (
              <Center
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: 40,
                }}
              >
                <Spinner centered variant="default" size="md" />
              </Center>
            ) : (
              <Box marginHorizontal={8} marginTop={4} gap={10} paddingBottom={8}>
                {eligibleJobs.map((job) => (
                  <EligibleJobCard
                    key={job.id}
                    job={job}
                    isSelected={selectedJobIds.includes(job.id)}
                    onSelect={handleJobSelect}
                    disabled={isInviting}
                    onCardPress={onCardPress}
                  />
                ))}

                {isFetchingNextPage && (
                  <Center
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      padding: 16,
                    }}
                  >
                    <Spinner centered variant="default" size="md" />
                  </Center>
                )}
              </Box>
            )}
          </Box>

          <Row gap={16} justifyContent="center" marginTop={16}>
            <Button
              dataCy="rating-success-no-thanks-button"
              style={{
                alignSelf: 'center',
                width: 186,
                margin: 0,
                backgroundColor: colors.secondary[500],
              }}
              onClick={handleNoThanks}
              text="No Thanks"
              disabled={isInviting}
            />

            <Button
              dataCy="rating-success-invite-button"
              text={
                selectedJobIds.length > 0
                  ? `Invite for ${selectedJobIds.length} Shift${selectedJobIds.length !== 1 ? 's' : ''}`
                  : 'Invite'
              }
              onClick={handleInviteClick}
              disabled={selectedJobIds.length === 0}
              style={{
                alignSelf: 'center',
                width: 186,
                margin: 0,
                backgroundColor:
                  selectedJobIds.length > 0 ? colors.primary[500] : colors.primary[200],
              }}
              isLoading={isInviting}
            />
          </Row>
        </Box>
      </Box>
    </Portal>
  );
};
